import { benefitService } from '@hixme/api'
import axios from 'axios'
import { actions as notificationActions } from 'modules/app-core/notification-manager'
import pako from 'pako'
import { toast } from 'react-toastify'
import { reset } from 'redux-form'
import { callWaiter } from 'redux-waiter'
import { actions as userSessionActions } from 'store/modules/user-session'
import apolloClient from '../../apollo/client'
import { SEND_APPLICATION_SUBMISSION_EVENT } from '../../apollo/queries'
import apiClient from '../../enrollment-platform/api/enrollment-platform-client/api-client'
import epsClient from '../../enrollment-platform/api/eps.client'
import { remapApplicationResponse } from './remapApplicationResponse'

// project
import { notification } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import { updateBenefitRealtimeCascadeStatus } from 'modules/benefits-realtime'
import t from './actionTypes'
import {
  GET_ALL_APPLICATIONS,
  GET_APPLICATION,
  POST_APPLICATION_SUBMISSION_EVENT,
  PUT_APPLICATION,
  STORAGE_FILTER,
  UPDATE_APPLICATION_ASSIGNEE,
} from './constants'

// output format YYYY-MM-DD
const formatDateString = (dateStr) => {
  if (!dateStr) return dateStr

  if (dayjs(dateStr, 'YYYY-MM-DD', true).isValid()) {
    return dayjs(dateStr).format('YYYY-MM-DD')
  }

  if (dayjs(dateStr, 'MM-DD-YYYY', true).isValid()) {
    return dayjs(dateStr, 'MM-DD-YYYY').format('YYYY-MM-DD')
  }
}

const handleApplicationsPayload = (payload) => {
  if (payload.Compressed) {
    // Decode the base64 string, str -> char[] -> byte[], decompress the data
    const strData = atob(payload.Data)
    const charData = strData.split('').map((x) => {
      return x.charCodeAt(0)
    })
    const binData = new Uint8Array(charData)
    // Decompress the data with pako
    const decompressedData = JSON.parse(
      pako.inflate(binData, {
        to: 'string',
      })
    ).Applications
    return decompressedData
  } else {
    // For legacy response shape, from pre-compression
    return payload.Data ? payload.Data.Applications : payload.Applications
  }
}

const getCurrentUser = (getState) => {
  const state = getState()
  const credentials = state?.authui?.credentials ?? {}
  return credentials.username || credentials.email || null
}

export const updateLocalApplication =
  ({ EmployeeName, ...application }) =>
  async (dispatch) => {
    dispatch(updateApplication(application))
    notification.success({
      message: `${EmployeeName} application was updated`,
      duration: 2,
    })
  }

// Application engine waiter calls
export const getAllApplications = (params) => (dispatch) =>
  dispatch(
    callWaiter(GET_ALL_APPLICATIONS, {
      requestCreator: () => {
        // format the BenefitEffictiveDate string in params
        const {
          BenefitEffectiveDate: bed,
          BenefitEffectiveDateRange: bedRange,
        } = params ?? {}
        let p = null
        if (bedRange?.length === 2) {
          const benefitEffectiveDate = formatDateString(bedRange[0])
          const benefitEffectiveDateEnd = formatDateString(bedRange[1])
          p =
            params && benefitEffectiveDate && benefitEffectiveDateEnd
              ? {
                  ...params,
                  BenefitEffectiveDate: benefitEffectiveDate,
                  BenefitEffectiveDateEnd: benefitEffectiveDateEnd,
                }
              : null
          if (p) delete p.BenefitEffectiveDateRange
        } else if (bed) {
          const benefitEffectiveDate = formatDateString(bed)
          p =
            params && benefitEffectiveDate
              ? { ...params, BenefitEffectiveDate: benefitEffectiveDate }
              : null
        }

        return benefitService({
          route: 'applications',
          ...(p && { params: p }), // params are optional
        })
          .then((response) => {
            if (response.JsonDataUrl) {
              // create a new axios instance to avoid sending default Authorization header
              const axiosInstance = axios.create()

              return axiosInstance
                .get(response.JsonDataUrl)
                .then((s3Response) => {
                  const data = handleApplicationsPayload({
                    ...s3Response,
                    Data: s3Response.data,
                    Compressed: response.Compressed,
                  })
                  dispatch(loadApplications(data))
                  return data
                })
            } else {
              const data = handleApplicationsPayload(response)
              dispatch(loadApplications(data))
              return data
            }
          })
          .catch((err) => {
            console.error('Error while retrieving applications')
            throw err
          })
      },
    })
  )

export const getApplication = (ApplicationPublicKey) => (dispatch) =>
  dispatch(
    callWaiter(`${GET_APPLICATION}-${ApplicationPublicKey}`, {
      requestCreator: () =>
        epsClient
          .get(`/core/benefits/applications/${ApplicationPublicKey}`)
          .then(({ data }) => remapApplicationResponse(data)),
    })
  )

export const update = (body) => (dispatch) =>
  dispatch(
    callWaiter(
      `${PUT_APPLICATION}-${body.ApplicationPublicKey}-${body.Status}`,
      {
        requestCreator: () =>
          benefitService({
            route: 'applications',
            body,
            method: 'PUT',
          }),
      }
    )
  )

export const updateStatus = (body, SkipEmail) => (dispatch, getState) => {
  // Prepare cascade-status update payload (non-blocking)
  const updateCascadeStatus = async () => {
    try {
      const currentUser = getCurrentUser(getState)
      const benefitId = body.ApplicationPublicKey
      const status = body.Status
      const statusDate = body.StatusDate
        ? new Date(body.StatusDate)
        : new Date()

      // Prepare cascade-status payload
      const cascadeStatusData = {
        status,
        statusDate,
        ...(currentUser && { updatedBy: currentUser }),
      }

      // Update cascade-status endpoint (non-blocking, errors are logged but don't fail)
      await updateBenefitRealtimeCascadeStatus(benefitId, cascadeStatusData)
    } catch (error) {
      // Log error but don't block the legacy API call
      console.error('Failed to update benefit cascade status:', error)
    }
  }

  // Start cascade-status update (fire and forget)
  updateCascadeStatus()

  // Continue with legacy API call
  return dispatch(
    callWaiter(
      `${PUT_APPLICATION}-${body.ApplicationPublicKey}-${body.Status}`,
      {
        requestCreator: () =>
          benefitService({
            route: 'applications/status',
            body,
            method: 'PUT',
            params: {
              SkipEmail,
            },
          }),
      }
    )
  )
}

export const updateAssignee = (body) => (dispatch) =>
  dispatch(
    callWaiter(UPDATE_APPLICATION_ASSIGNEE, {
      requestCreator: async () => {
        await apiClient.put('/benefits/assignee', body)
        await postActivityLog({
          action: ActivityLogAction.ASSIGN_APPLICATIONS_TO_ASSIGNEE,
          description: `assign ${body.benefits.length} ${body.benefits.length > 1 ? 'applications' : 'application'} to ${body.assignee}`,
          additionalData: {
            assignee: body.assignee,
            benefits: body.benefits,
          },
        })
        toast.success(
          `${body?.benefits?.length} Applications assigned to ${body.assignee} successfully, please refresh the page to see the changes.`,
          { position: toast.POSITION.TOP_RIGHT }
        )
        dispatch(setSelectedApplications([]))
      },
    })
  )

export const sendApplicationSubmittedEvent = (params) => async (dispatch) =>
  dispatch(
    callWaiter(
      `${POST_APPLICATION_SUBMISSION_EVENT}-${params.clientPublicKey}-${
        params.employeePublicKey
      }-${params.enrollmentPublicKey}`,
      {
        requestCreator: async () => {
          await apolloClient
            .mutate({
              mutation: SEND_APPLICATION_SUBMISSION_EVENT,
              variables: {
                input: {
                  clientPublicKey: params.clientPublicKey,
                  employeePublicKey: params.employeePublicKey,
                  enrollmentPublicKey: params.enrollmentPublicKey,
                  benefitYear: params.benefitYear,
                },
              },
            })
            .then((data) => ({
              type: POST_APPLICATION_SUBMISSION_EVENT,
              payload: data,
            }))
            .catch((err) => {
              console.error('Error while sending application submission event')
              throw err
            })
        },
      }
    )
  )

export const loadApplication = (event) => ({
  type: t.LOAD_APPLICATIONS,
  payload: event,
})

// ---------- search related action creators and async actions ---------- //

export const viewMore = () => ({
  type: t.VIEW_MORE,
})

export const viewAll = (value) => ({
  type: t.VIEW_ALL,
  payload: value,
})

export const toggleFilters = (event) => {
  // prevent navigation to # on filtered x button
  event && event.preventDefault()
  return {
    type: t.TOGGLE_FILTERS,
  }
}

export const updateBenefitEffectiveDate = (payload) => ({
  type: t.UPDATE_BENEFIT_EFFECTIVE_DATE,
  payload,
})

export const updateFilter = (values) => ({
  type: t.UPDATE_FILTER,
  payload: values,
})

export const updateNeedsAttention = (payload) => ({
  type: t.UPDATE_NEEDS_ATTENTION,
  payload,
})

export const updateNeedsCancellation = (payload) => ({
  type: t.UPDATE_NEEDS_CANCELLATION,
  payload,
})

export const setBenefitEffectiveDateChangeForQuery = (payload) => {
  return {
    type: t.SET_BENEFIT_EFFECTIVE_DATE_FOR_QUERY,
    payload,
  }
}

export const setBenefitEffectiveDateRangeChangeForQuery = (payload) => {
  return {
    type: t.SET_BENEFIT_EFFECTIVE_DATE_RANGE_FOR_QUERY,
    payload,
  }
}

export const updateCartOpen = (payload) => ({
  type: t.UPDATE_CART_OPEN,
  payload,
})

export const updateSameCarrier = (payload) => ({
  type: t.UPDATE_SAME_CARRIER,
  payload,
})

export const updateTestUsersFilter = (payload) => ({
  type: t.UPDATE_TEST_USER,
  payload,
})

export const updateCommissionFilter = (payload) => ({
  type: t.UPDATE_COMMISSION_FILTER,
  payload,
})

export const updateAssigneeFilter = (payload) => ({
  type: t.UPDATE_ASSIGNEE_FILTER,
  payload,
})

export const updateShowUnassigned = (payload) => ({
  type: t.UPDATE_SHOW_UNASSIGNED,
  payload,
})

export const loadSavedFilters = () => {
  try {
    const savedFilters = localStorage.getItem(STORAGE_FILTER)
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters)
      return {
        type: t.LOAD_SAVED_FILTERS,
        payload: parsedFilters,
      }
    }
  } catch (error) {
    console.error('Error loading saved filters:', error)
  }
  return {
    type: t.LOAD_SAVED_FILTERS,
    payload: null,
  }
}

export const resetComponent = (payload) => ({
  type: t.RESET_COMPONENT,
  payload,
})

export const updateKeyword = (event) => (dispatch) => {
  dispatch({
    type: t.UPDATE_KEYWORD,
    payload: event.target.value,
  })
}

export const resetFilters = () => (dispatch) => {
  dispatch({ type: t.RESET_FILTERS })
  dispatch(reset('ApplicationFilters'))
}

export const setCompanyId = (companyId) => ({
  type: t.SET_COMPANY_ID,
  payload: companyId,
})

export const onCompanyChange = (event) => (dispatch) => {
  const companyId = event.target.value
  dispatch(userSessionActions.setCurrentCompany(companyId))
  if (companyId) {
    dispatch(userSessionActions.setCurrentCompany(companyId))
  }
  dispatch(setCompanyId(companyId))
}
export const onCompanySelected = (companies) => (dispatch) => {
  dispatch(setCompanyId(companies?.map((company) => company.value)))
}

export const changeSort = (sortBy) => ({
  type: t.CHANGE_SORT,
  payload: sortBy,
})

export const loadApplications = (applications) => ({
  type: t.LOAD_APPLICATIONS,
  payload: applications,
})

export const updateApplication = (application) => ({
  type: t.UPDATE_APPLICATION,
  payload: application,
})

export const createRefreshNotification = (refreshMessage) => (dispatch) =>
  dispatch(notificationActions.createSuccessNotification(refreshMessage))

export const setSelectedApplications = (selectedApplications) => ({
  type: t.SET_SELECTED_APPLICATIONS,
  payload: selectedApplications,
})

export const updateCarrierPlanChange = (payload) => ({
  type: t.UPDATE_CARRIER_PLAN_CHANGE,
  payload,
})
